/**
 * 폼 입력 부담 감소용 사전 옵션 데이터.
 *
 * 모든 옵션은 "기타 (직접 입력)" 으로 fallback 가능 — DropdownWithCustom 컴포넌트가 처리.
 * 실제 사용자 데이터(직원 명부 등)와 연동하기 전 임시 표준 목록.
 */

export interface Opt { value: string; label: string; labelEn?: string }

// ── 업종 (안전교육·JSA·TBM 공통) — 한국표준산업분류 + 산업안전 표준 ─────
export const INDUSTRY_OPTIONS: readonly Opt[] = [
  { value: '건설업', label: '건설업 (토목·건축·설비)', labelEn: 'Construction (Civil · Building · Facilities)' },
  { value: '제조업', label: '제조업 (기계·금속 가공)', labelEn: 'Manufacturing (Machinery · Metalworking)' },
  { value: '화학산업', label: '화학산업 (석유화학·정밀화학)', labelEn: 'Chemical (Petrochemical · Fine Chemicals)' },
  { value: '전기·전자', label: '전기·전자 (반도체·디스플레이)', labelEn: 'Electrical · Electronics (Semiconductor · Display)' },
  { value: '자동차', label: '자동차 (조립·부품)', labelEn: 'Automotive (Assembly · Parts)' },
  { value: '조선·중공업', label: '조선·중공업', labelEn: 'Shipbuilding · Heavy Industry' },
  { value: '제철·금속', label: '제철·금속 (제련·압연)', labelEn: 'Steel · Metals (Smelting · Rolling)' },
  { value: '식품·음료', label: '식품·음료', labelEn: 'Food · Beverage' },
  { value: '제약·바이오', label: '제약·바이오', labelEn: 'Pharmaceutical · Biotech' },
  { value: '에너지·발전', label: '에너지·발전 (LNG·LPG·발전소)', labelEn: 'Energy · Power Generation (LNG · LPG · Plants)' },
  { value: '물류·운송', label: '물류·운송 (창고·하역)', labelEn: 'Logistics · Transport (Warehouse · Handling)' },
  { value: '서비스업', label: '서비스업 (사무·영업)', labelEn: 'Services (Office · Sales)' },
  { value: '농림·수산', label: '농림·수산', labelEn: 'Agriculture · Forestry · Fishery' },
  { value: '폐기물처리', label: '폐기물처리·환경', labelEn: 'Waste Management · Environmental' },
  { value: '광업', label: '광업·채석', labelEn: 'Mining · Quarrying' },
  { value: '의료·요양', label: '의료·요양', labelEn: 'Healthcare · Nursing' },
] as const

/** 사업장 KSIC 카테고리(data/ksic.ts) → 교육 업종 옵션 값 — 사업장 탭 임베드 시 프리필용.
 *  시스템이 이미 아는 업종을 다시 묻지 않기 위한 근사 매핑. 사용자가 언제든 바꿀 수 있다. */
export const EDU_INDUSTRY_BY_KSIC: Record<string, string> = {
  manufacturing: '제조업',
  construction: '건설업',
  chemical: '화학산업',
  mining: '광업',
  electricity_gas: '에너지·발전',
  environment: '폐기물처리',
  agriculture: '농림·수산',
  transport: '물류·운송',
  wholesale_retail: '서비스업',
  food_lodging: '식품·음료',
  healthcare: '의료·요양',
  it: '서비스업',
  education: '서비스업',
  finance: '서비스업',
  realestate: '서비스업',
  professional: '서비스업',
  facility_support: '서비스업',
  arts_sports: '서비스업',
  service: '서비스업',
}

// ── 직종 (재해조사표·작업허가서 작업자 직종) — 한국표준직업분류 기반 ─
export const OCCUPATION_OPTIONS: readonly Opt[] = [
  { value: '생산직', label: '생산직 (조립·가공)', labelEn: 'Production (Assembly · Processing)' },
  { value: '용접·금속가공', label: '용접·금속가공', labelEn: 'Welding · Metalworking' },
  { value: '전기기사', label: '전기기사·전공', labelEn: 'Electrician' },
  { value: '기계정비', label: '기계정비·설비', labelEn: 'Mechanical Maintenance · Facilities' },
  { value: '계측제어', label: '계측제어', labelEn: 'Instrumentation · Control' },
  { value: '운전원', label: '운전원 (공정)', labelEn: 'Operator (Process)' },
  { value: '비계공', label: '비계공', labelEn: 'Scaffolder' },
  { value: '도장공', label: '도장공', labelEn: 'Painter' },
  { value: '굴착작업', label: '굴착·토목 작업', labelEn: 'Excavation · Civil Work' },
  { value: '운반·하역', label: '운반·하역', labelEn: 'Material Handling' },
  { value: '청소·환경', label: '청소·환경 미화', labelEn: 'Cleaning · Sanitation' },
  { value: '경비·안전', label: '경비·안전관리', labelEn: 'Security · Safety Management' },
  { value: '기타 기능직', label: '기타 기능직', labelEn: 'Other Skilled Trade' },
  { value: '사무직', label: '사무직', labelEn: 'Office Worker' },
  { value: '연구직', label: '연구직', labelEn: 'Researcher' },
  { value: '관리직', label: '관리직', labelEn: 'Management' },
] as const

// ── 부서 ────────────────────────────────────────────────────
export const DEPARTMENT_OPTIONS: readonly Opt[] = [
  { value: '안전관리팀',     label: '안전관리팀',     labelEn: 'Safety Management' },
  { value: '생산팀',         label: '생산팀',         labelEn: 'Production' },
  { value: '제조팀',         label: '제조팀',         labelEn: 'Manufacturing' },
  { value: '설비팀',         label: '설비팀',         labelEn: 'Facilities' },
  { value: '정비팀',         label: '정비팀',         labelEn: 'Maintenance' },
  { value: '품질관리팀',     label: '품질관리팀',     labelEn: 'Quality Control' },
  { value: '환경관리팀',     label: '환경관리팀',     labelEn: 'Environmental Management' },
  { value: '시설관리팀',     label: '시설관리팀',     labelEn: 'Facility Management' },
  { value: '공무팀',         label: '공무팀',         labelEn: 'Engineering Services' },
  { value: '전기팀',         label: '전기팀',         labelEn: 'Electrical' },
  { value: '기계팀',         label: '기계팀',         labelEn: 'Mechanical' },
  { value: '계전팀',         label: '계전팀',         labelEn: 'Instrumentation & Relay' },
  { value: '협력업체',       label: '협력업체',       labelEn: 'Subcontractor' },
  { value: '연구개발팀',     label: '연구개발팀',     labelEn: 'R&D' },
  { value: '관리팀',         label: '관리팀',         labelEn: 'Administration' },
] as const

// ── 직책 ────────────────────────────────────────────────────
export const POSITION_OPTIONS: readonly Opt[] = [
  { value: '사원',       label: '사원',       labelEn: 'Staff' },
  { value: '주임',       label: '주임',       labelEn: 'Associate' },
  { value: '대리',       label: '대리',       labelEn: 'Assistant Manager' },
  { value: '과장',       label: '과장',       labelEn: 'Manager' },
  { value: '차장',       label: '차장',       labelEn: 'Deputy General Manager' },
  { value: '부장',       label: '부장',       labelEn: 'General Manager' },
  { value: '팀장',       label: '팀장',       labelEn: 'Team Leader' },
  { value: '반장',       label: '반장',       labelEn: 'Group Leader' },
  { value: '조장',       label: '조장',       labelEn: 'Crew Leader' },
  { value: '현장소장',   label: '현장소장',   labelEn: 'Site Manager' },
  { value: '안전관리자', label: '안전관리자', labelEn: 'Safety Manager' },
  { value: '관리감독자', label: '관리감독자', labelEn: 'Supervisor' },
  { value: '작업지휘자', label: '작업지휘자', labelEn: 'Work Supervisor' },
] as const

// ── 작업 종류 — 산업안전 표준 작업 분류 (산업안전보건법 위험작업 + 일반작업 통합) ────
export const WORK_TYPE_OPTIONS: readonly Opt[] = [
  { value: '정비·보수',     label: '정비·보수 (설비 점검·교체)', labelEn: 'Maintenance · Repair (Inspection · Replacement)' },
  { value: '점검',          label: '점검 (예방·정기)', labelEn: 'Inspection (Preventive · Routine)' },
  { value: '청소',          label: '청소·세척', labelEn: 'Cleaning · Washing' },
  { value: '운반·하역',     label: '운반·하역', labelEn: 'Material Handling' },
  { value: '설치·시공',     label: '설치·시공', labelEn: 'Installation · Construction' },
  { value: '해체·철거',     label: '해체·철거', labelEn: 'Dismantling · Demolition' },
  { value: '용접·절단',     label: '용접·절단 (화기작업)', labelEn: 'Welding · Cutting (Hot Work)' },
  { value: '도장',          label: '도장·코팅', labelEn: 'Painting · Coating' },
  { value: '굴착',          label: '굴착·토공', labelEn: 'Excavation · Earthwork' },
  { value: '운전·조작',     label: '운전·조작 (공정·기계)', labelEn: 'Operation (Process · Machinery)' },
  { value: '전기작업',      label: '전기작업 (활선·정전)', labelEn: 'Electrical Work (Live · De-energized)' },
  { value: '고소작업',      label: '고소작업 (높이 2m 이상)', labelEn: 'Work at Height (2m+)' },
  { value: '밀폐공간 작업', label: '밀폐공간 작업', labelEn: 'Confined Space Work' },
  { value: '크레인 작업',   label: '크레인·양중작업', labelEn: 'Crane · Lifting Work' },
  { value: '지게차 작업',   label: '지게차 운반', labelEn: 'Forklift Operation' },
  { value: '중량물 취급',   label: '중량물 취급', labelEn: 'Heavy Load Handling' },
  { value: '화학물질 취급', label: '화학물질 취급', labelEn: 'Chemical Handling' },
  { value: '방사선 작업',   label: '방사선 작업', labelEn: 'Radiation Work' },
  { value: '시운전',        label: '시운전·시험', labelEn: 'Commissioning · Testing' },
] as const

// ── 환경·위험 조건 (체크박스 — 복수 선택) ────────────────────
export const WORK_CONDITION_OPTIONS: readonly Opt[] = [
  { value: '고소작업', label: '고소작업 (높이 2m+)', labelEn: 'Work at Height (2m+)' },
  { value: '밀폐공간', label: '밀폐공간', labelEn: 'Confined Space' },
  { value: '화기작업', label: '화기작업 (용접·그라인딩)', labelEn: 'Hot Work (Welding · Grinding)' },
  { value: '정전기 위험', label: '정전기 위험', labelEn: 'Static Electricity Hazard' },
  { value: '굴착작업', label: '굴착작업', labelEn: 'Excavation Work' },
  { value: '중량물 취급', label: '중량물 취급 (25kg+)', labelEn: 'Heavy Load Handling (25kg+)' },
  { value: '유해화학물질', label: '유해화학물질 취급', labelEn: 'Hazardous Chemical Handling' },
  { value: '소음 (85dB+)', label: '소음 (85dB+)', labelEn: 'Noise (85dB+)' },
  { value: '고온', label: '고온 (40°C+)', labelEn: 'High Temperature (40°C+)' },
  { value: '저온', label: '저온 (5°C-)', labelEn: 'Low Temperature (≤5°C)' },
  { value: '야간작업', label: '야간작업', labelEn: 'Night Work' },
  { value: '단독작업', label: '단독작업', labelEn: 'Lone Work' },
  { value: '다업종 동시작업', label: '다업종 동시작업', labelEn: 'Multi-trade Concurrent Work' },
  { value: '협소공간', label: '협소공간', labelEn: 'Tight Space' },
  { value: '강풍·우천', label: '강풍·우천 옥외', labelEn: 'Strong Wind · Rainy Outdoor' },
  { value: '활선 작업', label: '활선 작업 (전원 인가)', labelEn: 'Live-line Work (Energized)' },
] as const

// ── 사용 장비 / 도구 (체크박스) ──────────────────────────
export const EQUIPMENT_OPTIONS: readonly Opt[] = [
  { value: '비계',        label: '비계·작업발판', labelEn: 'Scaffold · Work Platform' },
  { value: '사다리',      label: '사다리', labelEn: 'Ladder' },
  { value: '고소작업대',  label: '고소작업대 (시저·붐 리프트)', labelEn: 'Aerial Work Platform (Scissor · Boom Lift)' },
  { value: '크레인',      label: '크레인 (이동식·천장)', labelEn: 'Crane (Mobile · Overhead)' },
  { value: '지게차',      label: '지게차', labelEn: 'Forklift' },
  { value: '굴착기',      label: '굴착기', labelEn: 'Excavator' },
  { value: '용접기',      label: '용접기', labelEn: 'Welding Machine' },
  { value: '그라인더',    label: '그라인더', labelEn: 'Grinder' },
  { value: '드릴',        label: '전동 드릴', labelEn: 'Power Drill' },
  { value: '컨베이어',    label: '컨베이어', labelEn: 'Conveyor' },
  { value: '안전벨트',    label: '안전벨트·죔줄', labelEn: 'Safety Harness · Lanyard' },
  { value: '방진마스크',  label: '방진·방독 마스크', labelEn: 'Dust · Respirator Mask' },
  { value: '가스감지기',  label: '가스 감지기', labelEn: 'Gas Detector' },
  { value: '소화기',      label: '소화기·소화전', labelEn: 'Fire Extinguisher · Hydrant' },
  { value: '비상정지장치', label: '비상정지장치 (E-Stop)', labelEn: 'Emergency Stop (E-Stop)' },
  { value: '체인블록',    label: '체인블록·호이스트', labelEn: 'Chain Block · Hoist' },
] as const

// ── 작업 인원 ───────────────────────────────────────────
export const WORKER_COUNT_OPTIONS: readonly Opt[] = [
  { value: '1명 (단독)', label: '1명 (단독)', labelEn: '1 (Lone)' },
  { value: '2명',        label: '2명',        labelEn: '2' },
  { value: '3명',        label: '3명',        labelEn: '3' },
  { value: '4~5명',      label: '4~5명',      labelEn: '4-5' },
  { value: '6~10명',     label: '6~10명',     labelEn: '6-10' },
  { value: '11~20명',    label: '11~20명',    labelEn: '11-20' },
  { value: '21명 이상',  label: '21명 이상',  labelEn: '21+' },
] as const

// ── 작업 장소 — 산업안전 일반 분류 (회사별 specific 명칭은 "기타" 로 입력) ─
export const LOCATION_OPTIONS: readonly Opt[] = [
  { value: '공장동 내부',        label: '공장동 내부 (생산라인)', labelEn: 'Inside Factory (Production Line)' },
  { value: '옥외 작업장',        label: '옥외 작업장', labelEn: 'Outdoor Worksite' },
  { value: '저장탱크 구역',      label: '저장탱크 구역', labelEn: 'Storage Tank Area' },
  { value: '반응기실',          label: '반응기실', labelEn: 'Reactor Room' },
  { value: '보일러실',          label: '보일러실', labelEn: 'Boiler Room' },
  { value: '변전실',           label: '변전실·전기실', labelEn: 'Substation · Electrical Room' },
  { value: '폐수처리장',        label: '폐수처리장', labelEn: 'Wastewater Treatment Plant' },
  { value: '저장창고',          label: '저장창고', labelEn: 'Storage Warehouse' },
  { value: '하역장',           label: '하역장·상하차 구역', labelEn: 'Loading · Unloading Area' },
  { value: '옥상',             label: '옥상', labelEn: 'Rooftop' },
  { value: '지하 피트',         label: '지하 피트', labelEn: 'Underground Pit' },
  { value: '맨홀 내부',         label: '맨홀 내부', labelEn: 'Inside Manhole' },
  { value: '밀폐공간',          label: '밀폐공간 (탱크·사일로 등)', labelEn: 'Confined Space (Tank · Silo, etc.)' },
  { value: '굴착 작업장',        label: '굴착 작업장', labelEn: 'Excavation Site' },
  { value: '비계·고소작업대',    label: '비계·고소작업대', labelEn: 'Scaffold · Aerial Work Platform' },
] as const

// ── 위험요인 카테고리 (KOSHA 표준 분류) ──────────────────────
export const HAZARD_CATEGORY_OPTIONS: readonly Opt[] = [
  { value: '추락',   label: '추락 (떨어짐)', labelEn: 'Fall from Height' },
  { value: '전도',   label: '전도 (넘어짐)', labelEn: 'Slip/Trip/Fall' },
  { value: '협착',   label: '협착 (끼임)', labelEn: 'Caught-between' },
  { value: '충돌',   label: '충돌 (부딪힘)', labelEn: 'Struck-by Collision' },
  { value: '낙하·비래', label: '낙하·비래 (물체에 맞음)', labelEn: 'Falling Object Strike' },
  { value: '붕괴',   label: '붕괴 (무너짐)', labelEn: 'Collapse' },
  { value: '감전',   label: '감전', labelEn: 'Electrocution' },
  { value: '화재·폭발', label: '화재·폭발', labelEn: 'Fire · Explosion' },
  { value: '화학물질 노출', label: '화학물질 노출 (중독·질식)', labelEn: 'Chemical Exposure (Poisoning · Asphyxiation)' },
  { value: '산소결핍', label: '산소결핍 (질식)', labelEn: 'Oxygen Deficiency (Asphyxiation)' },
  { value: '소음',   label: '소음', labelEn: 'Noise' },
  { value: '진동',   label: '진동', labelEn: 'Vibration' },
  { value: '고온',   label: '고온 (화상)', labelEn: 'Heat (Burn)' },
  { value: '저온',   label: '저온 (동상)', labelEn: 'Cold (Frostbite)' },
  { value: '방사선', label: '방사선', labelEn: 'Radiation' },
  { value: '근골격계', label: '근골격계 (들기·반복)', labelEn: 'Musculoskeletal (Lifting · Repetitive)' },
  { value: '교통사고', label: '교통사고', labelEn: 'Traffic Accident' },
] as const

// ── 상해 종류 (재해조사표) ────────────────────────────────────
export const INJURY_TYPE_OPTIONS: readonly Opt[] = [
  { value: '골절',         label: '골절',           labelEn: 'Fracture' },
  { value: '베임·찢김',    label: '베임·찢김 (열상)', labelEn: 'Cut · Laceration' },
  { value: '찰과상',       label: '찰과상',         labelEn: 'Abrasion' },
  { value: '타박상',       label: '타박상',         labelEn: 'Contusion' },
  { value: '염좌',         label: '염좌·삠',        labelEn: 'Sprain' },
  { value: '절단',         label: '절단',           labelEn: 'Amputation' },
  { value: '화상',         label: '화상',           labelEn: 'Burn' },
  { value: '동상',         label: '동상',           labelEn: 'Frostbite' },
  { value: '감전상',       label: '감전상',         labelEn: 'Electric Shock Injury' },
  { value: '질식',         label: '질식',           labelEn: 'Asphyxiation' },
  { value: '중독',         label: '중독',           labelEn: 'Poisoning' },
  { value: '청력손상',     label: '청력손상',       labelEn: 'Hearing Loss' },
  { value: '시력손상',     label: '시력손상',       labelEn: 'Vision Damage' },
  { value: '근골격계 질환', label: '근골격계 질환',   labelEn: 'Musculoskeletal Disorder' },
  { value: '뇌진탕',       label: '뇌진탕',         labelEn: 'Concussion' },
  { value: '내장 손상',    label: '내장 손상',      labelEn: 'Internal Organ Injury' },
  { value: '복합 외상',    label: '복합 외상',      labelEn: 'Multiple Trauma' },
] as const

// ── 손상 부위 (재해조사표) ────────────────────────────────────
export const BODY_PART_OPTIONS: readonly Opt[] = [
  { value: '머리',         label: '머리',          labelEn: 'Head' },
  { value: '얼굴',         label: '얼굴',          labelEn: 'Face' },
  { value: '눈',           label: '눈',            labelEn: 'Eye' },
  { value: '귀',           label: '귀',            labelEn: 'Ear' },
  { value: '목',           label: '목',            labelEn: 'Neck' },
  { value: '어깨',         label: '어깨',          labelEn: 'Shoulder' },
  { value: '팔',           label: '팔',            labelEn: 'Arm' },
  { value: '손·손가락',    label: '손·손가락',     labelEn: 'Hand · Finger' },
  { value: '가슴',         label: '가슴',          labelEn: 'Chest' },
  { value: '복부',         label: '복부',          labelEn: 'Abdomen' },
  { value: '허리',         label: '허리',          labelEn: 'Lower Back' },
  { value: '등',           label: '등',            labelEn: 'Back' },
  { value: '엉덩이',       label: '엉덩이',        labelEn: 'Hip' },
  { value: '다리',         label: '다리',          labelEn: 'Leg' },
  { value: '발·발가락',    label: '발·발가락',     labelEn: 'Foot · Toe' },
  { value: '전신',         label: '전신 (다발성)',  labelEn: 'Whole Body (Multiple)' },
  { value: '내부 장기',    label: '내부 장기',     labelEn: 'Internal Organs' },
] as const

// ── 안전조치 / 안전대책 표준 ────────────────────────────────
export const SAFEGUARD_OPTIONS: readonly Opt[] = [
  { value: '작업 전 위험성평가 실시',       label: '작업 전 위험성평가 실시',       labelEn: 'Pre-work Risk Assessment' },
  { value: 'TBM (작업 전 안전점검회의)',     label: 'TBM (작업 전 안전점검회의)',     labelEn: 'TBM (Pre-work Safety Meeting)' },
  { value: '작업허가서 발행',              label: '작업허가서 발행',              labelEn: 'Work Permit Issuance' },
  { value: '안전난간·낙하방지망 설치',      label: '안전난간·낙하방지망 설치',      labelEn: 'Guardrail · Fall Net Installation' },
  { value: '안전대 착용 및 부착설비 사용',  label: '안전대 착용 및 부착설비 사용',  labelEn: 'Safety Harness with Anchor Use' },
  { value: '개인보호구 착용',              label: '개인보호구 착용 (PPE)',          labelEn: 'Personal Protective Equipment (PPE)' },
  { value: '가스농도 측정',                label: '가스농도 측정 (산소·가연성·유해)', labelEn: 'Gas Concentration Measurement (O₂ · Flammable · Toxic)' },
  { value: '환기 설비 가동',               label: '환기 설비 가동',               labelEn: 'Ventilation System Operation' },
  { value: '전원 차단(LOTO)',              label: '전원 차단(LOTO·잠금태그)',      labelEn: 'Power Isolation (LOTO · Lockout/Tagout)' },
  { value: '화기작업 시 소화기 비치',       label: '화기작업 시 소화기 비치',       labelEn: 'Fire Extinguisher for Hot Work' },
  { value: '작업구역 출입통제',             label: '작업구역 출입통제',             labelEn: 'Work Area Access Control' },
  { value: '감시인 배치',                  label: '감시인 배치',                  labelEn: 'Safety Watcher Assignment' },
  { value: '비상연락 체계 확보',            label: '비상연락 체계 확보',            labelEn: 'Emergency Communication System' },
  { value: '교육·자격 확인',               label: '교육·자격 확인 (특별안전교육)',  labelEn: 'Training · Certification Verification (Special Safety Training)' },
  { value: '기계 방호장치·안전덮개 설치',   label: '기계 방호장치·안전덮개 설치 (끼임·말림 방지)', labelEn: 'Machine Guard · Safety Cover' },
  { value: '안전검사·정기점검 실시',        label: '안전검사·정기점검 실시 (위험기계)', labelEn: 'Safety Inspection · Periodic Check' },
  { value: '줄걸이·인양로프 점검',          label: '줄걸이·인양로프 점검 (중량물 인양)', labelEn: 'Sling · Lifting Rope Inspection' },
  { value: '접지·누전차단기 설치',          label: '접지·누전차단기 설치',          labelEn: 'Grounding · Earth Leakage Breaker' },
  { value: '국소배기장치 설치·가동',        label: '국소배기장치 설치·가동 (분진·흄)', labelEn: 'Local Exhaust Ventilation' },
  { value: '2인 1조 작업',                 label: '2인 1조 작업',                 labelEn: 'Two-Person Buddy System' },
  { value: '정리정돈(4S)·통로 확보',        label: '정리정돈(4S)·통로 확보',        labelEn: 'Housekeeping (4S) · Clear Passage' },
  { value: '적재·결박 기준 준수',           label: '적재·결박 기준 준수',           labelEn: 'Loading · Lashing Standard' },
  { value: '안전표지·경고표지 설치',        label: '안전표지·경고표지 설치',        labelEn: 'Safety · Warning Signage' },
  { value: '방폭 전기설비 사용',            label: '방폭 전기설비 사용 (폭발위험장소)', labelEn: 'Explosion-Proof Electrical Equipment' },
  { value: '중량물 취급 보조기구 사용',     label: '중량물 취급 보조기구 사용 (근골격계)', labelEn: 'Material Handling Aids' },
  { value: '후방감지·경광등·후진경보',      label: '후방감지·경광등·후진경보 (차량·중장비)', labelEn: 'Rear Sensor · Beacon · Backup Alarm' },
] as const

// ── 보호구 (PPE) ────────────────────────────────────────────
export const PPE_OPTIONS: readonly Opt[] = [
  { value: '안전모',     label: '안전모',           labelEn: 'Hard Hat' },
  { value: '안전화',     label: '안전화',           labelEn: 'Safety Shoes' },
  { value: '안전대',     label: '안전대 (추락방지)', labelEn: 'Safety Harness (Fall Arrest)' },
  { value: '보안경',     label: '보안경',           labelEn: 'Safety Glasses' },
  { value: '보안면',     label: '보안면',           labelEn: 'Face Shield' },
  { value: '귀마개',     label: '귀마개·귀덮개',     labelEn: 'Earplugs · Earmuffs' },
  { value: '방진마스크', label: '방진마스크',        labelEn: 'Dust Mask' },
  { value: '방독마스크', label: '방독마스크',        labelEn: 'Respirator Mask' },
  { value: '송기마스크', label: '송기마스크 (밀폐공간)', labelEn: 'Supplied-Air Respirator (Confined Space)' },
  { value: '내화학복',   label: '내화학복·방수복',   labelEn: 'Chemical Suit · Waterproof Suit' },
  { value: '내전압장갑', label: '내전압장갑 (전기작업)', labelEn: 'Insulated Gloves (Electrical Work)' },
  { value: '용접장갑',   label: '용접장갑·앞치마',   labelEn: 'Welding Gloves · Apron' },
  { value: '구명조끼',   label: '구명조끼 (수상 작업)', labelEn: 'Life Jacket (Water Work)' },
  { value: '절단방지장갑', label: '절단방지장갑 (베임 방지)', labelEn: 'Cut-Resistant Gloves' },
  { value: '안전장갑',   label: '안전장갑 (일반)',    labelEn: 'Safety Gloves (General)' },
  { value: '방열복·방열장갑', label: '방열복·방열장갑 (고열작업)', labelEn: 'Heat-Resistant Suit · Gloves' },
  { value: '무릎보호대', label: '무릎보호대',        labelEn: 'Knee Pads' },
  { value: '제전복·정전기방지화', label: '제전복·정전기방지화', labelEn: 'Anti-Static Suit · Shoes' },
] as const

// ── 작업 단계 (JSA Step 보조 — 자유텍스트가 기본이지만 빠른 선택용) ──
export const WORK_STEP_OPTIONS: readonly Opt[] = [
  { value: '작업 사전 점검',     label: '작업 사전 점검',     labelEn: 'Pre-work Inspection' },
  { value: '공구·장비 준비',     label: '공구·장비 준비',     labelEn: 'Tool · Equipment Preparation' },
  { value: '작업구역 출입통제',   label: '작업구역 출입통제',   labelEn: 'Work Area Access Control' },
  { value: '대상 설비 격리',     label: '대상 설비 격리 (LOTO)', labelEn: 'Equipment Isolation (LOTO)' },
  { value: '본 작업 수행',       label: '본 작업 수행',       labelEn: 'Main Work Execution' },
  { value: '청소 및 정리',       label: '청소 및 정리',       labelEn: 'Cleanup · Housekeeping' },
  { value: '복구 및 시운전',     label: '복구 및 시운전',     labelEn: 'Restoration · Commissioning' },
  { value: '작업 후 점검',       label: '작업 후 점검',       labelEn: 'Post-work Inspection' },
] as const

// ── 회사 분류 (시정조치·재해조사) ─────────────────────────────
export const COMPANY_TYPE_OPTIONS: readonly Opt[] = [
  { value: '원청',         label: '원청 (도급인)',     labelEn: 'Principal Contractor' },
  { value: '협력업체',     label: '협력업체 (수급인)', labelEn: 'Subcontractor' },
  { value: '재하도급',     label: '재하도급',         labelEn: 'Sub-subcontractor' },
  { value: '파견',         label: '파견업체',         labelEn: 'Dispatched Worker' },
] as const
